import { useNavigate } from 'react-router-dom';

import { CalendarDays, MessageSquare } from 'lucide-react';

import type { RsrCard as Card, RsrState } from '@/api/schema';
import { Avatar } from '@/components/ui/Avatar';
import { PointsMeter } from '@/features/candidates/components/PointsMeter';
import { StatusChip } from '@/features/candidates/components/StatusChip';
import { RSR_LABEL } from '@/features/candidates/labels';
import { cx } from '@/lib/cx';
import { formatDate, type DateFormat } from '@/lib/dates';

import { canDropOnRsr } from '../api';
import styles from './RsrCard.module.css';

/**
 * One candidate on the RSR board.
 *
 * A near-twin of the Flyst card and deliberately not shared with it. The two boards
 * differ in what a card *is*: this one's column is read off a date rather than set, so
 * it shows the booking, and only some of its columns can be moved to at all. Folding
 * both into one component would mean a prop for each of those differences and a reader
 * having to hold both boards in their head to change either.
 */
export function RsrCard({
  card,
  dateFormat,
  moving,
  dragging,
  onOpen,
  onMove,
  onDragStart,
  onDragEnd,
}: {
  card: Card;
  dateFormat: DateFormat;
  moving: boolean;
  dragging: boolean;
  onOpen: (card: Card) => void;
  onMove: (card: Card, state: RsrState) => void;
  onDragStart: (card: Card) => void;
  onDragEnd: () => void;
}) {
  const navigate = useNavigate();

  return (
    <div
      className={cx(styles.card, moving && styles.moving, dragging && styles.dragging)}
      draggable={!moving}
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', card.id);
        onDragStart(card);
      }}
      onDragEnd={onDragEnd}
    >
      <button
        type="button"
        className={styles.body}
        onClick={() => {
          onOpen(card);
        }}
      >
        <Avatar name={card.full_name} size={30} />
        <span className={styles.who}>
          <span
            className={styles.name}
            role="link"
            tabIndex={0}
            onClick={(event) => {
              event.stopPropagation();
              void navigate(`/candidates/${card.id}`);
            }}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              event.stopPropagation();
              void navigate(`/candidates/${card.id}`);
            }}
          >
            {card.full_name}
          </span>
          {/* Only when there is something to say. A zero on every card is noise. */}
          {card.note_count > 0 ? (
            <span
              className={styles.notes}
              title={`${card.note_count} note${card.note_count === 1 ? '' : 's'}`}
            >
              <MessageSquare size={11} aria-hidden="true" />
              {card.note_count}
            </span>
          ) : null}
          <span className={styles.position}>
            {card.preferred_position?.name ?? (
              <span className={styles.none}>No position yet</span>
            )}
          </span>
        </span>
      </button>

      <div className={styles.meta}>
        <PointsMeter value={card.sponsor_points} />
        <StatusChip status={card.status} history={{ candidateId: card.id, dateFormat }} />
      </div>

      {/* The booking is why this card is in the column it is in, so it is on the card
          rather than a hover away. */}
      <p className={cx(styles.booking, card.rsr_state === 'happened' && styles.overdue)}>
        <CalendarDays size={12} aria-hidden="true" />
        {card.rsr_scheduled_on
          ? `${card.rsr_state === 'happened' ? 'Was booked' : 'Booked'} ${formatDate(card.rsr_scheduled_on, dateFormat)}`
          : 'No date booked'}
      </p>

      <label className={styles.moveLabel}>
        <span className="visually-hidden">Move {card.full_name} to</span>
        <select
          className={styles.move}
          value={card.rsr_state}
          disabled={moving}
          onChange={(event) => {
            onMove(card, event.target.value as RsrState);
          }}
        >
          {(Object.keys(RSR_LABEL) as RsrState[]).map((state) => (
            <option
              key={state}
              value={state}
              // Arranged needs a date and Happened is not a decision, so neither is
              // offered here. Both are still reachable: give the screening a date, or
              // let the day pass.
              disabled={state !== card.rsr_state && !canDropOnRsr(state)}
            >
              {state === card.rsr_state ? RSR_LABEL[state] : `Move to ${RSR_LABEL[state]}`}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}
